import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Card, Group, Stack, Switch, Text, TextInput, ThemeIcon, Tooltip } from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useBranchQuery, useUpdateBranchMutation } from '../../../../api/branches/api'
import { PhoneNumberInput } from '../../../../components/PhoneNumberInput/PhoneNumberInput'
import { removeCountryCode } from '../../../../components/PhoneNumberInput/phoneNumberUtils'
import PlacesAutoComplete from '../../../../components/PlacesAutocomplete'
import { useUser } from '../../../../user-provider/user-context'
import { removePhoneNumberFormatting } from '../../../../utils/phone-number-formatting'

const generalSettingsSchema = z.object({
  name: z.string().min(1, 'Branch name is required').max(100, 'Branch name must be 100 characters or less'),
  address: z.string().min(1, 'Address is required').max(200, 'Address must be 200 characters or less'),
  phone_number: z.string().min(1, 'Phone number is required').max(20, 'Phone number must be 20 characters or less'),
  disable_notifications_outside_working_hours: z.boolean(),
})

type GeneralFormData = z.infer<typeof generalSettingsSchema>

export const Route = createFileRoute('/app/settings/branch-settings/general')({
  component: GeneralSettingsComponent,
})

function GeneralSettingsComponent() {
  const { branchInfo } = useUser()
  const branchId = branchInfo?.provider_branch_id || ''
  const { data: branch, isLoading: branchLoading, isError: branchError } = useBranchQuery(branchId)
  const updateBranchMutation = useUpdateBranchMutation()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<GeneralFormData>({
    resolver: zodResolver(generalSettingsSchema),
    defaultValues: {
      name: '',
      address: '',
      phone_number: '',
      disable_notifications_outside_working_hours: false,
    },
  })

  useEffect(() => {
    if (branch) {
      reset({
        name: branch.name || '',
        address: branch.address || '',
        phone_number: removeCountryCode(branch.phone_number) || '',
        disable_notifications_outside_working_hours: branch.disable_notifications_outside_working_hours || false,
      })
    }
  }, [branch, reset])

  const onSubmit = (data: GeneralFormData) => {
    if (branchId) {
      updateBranchMutation.mutate({
        branchId,
        data: {
          name: data.name,
          address: data.address,
          phone_number: removePhoneNumberFormatting(data.phone_number), // Convert to machine-friendly format
          disable_notifications_outside_working_hours: data.disable_notifications_outside_working_hours,
        },
      })
    }
  }

  return (
    <Card className="general-settings-content">
      <Stack gap="0">
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Box pos="relative">
              <TextInput
                {...field}
                label="Branch name"
                placeholder={branchLoading ? 'Loading...' : 'Enter branch name'}
                disabled={branchLoading || branchError}
              />
              {errors.name?.message && <div className="absolute-error-message">{errors.name.message}</div>}
            </Box>
          )}
        />

        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <Box pos="relative">
              <PlacesAutoComplete
                {...field}
                value={branch?.address}
                label="Address"
                placeholder={branchLoading ? 'Loading...' : 'Enter branch address'}
                disabled={branchLoading || branchError}
              />
              {errors.address?.message && <div className="absolute-error-message">{errors.address.message}</div>}
            </Box>
          )}
        />

        <Controller
          name="phone_number"
          control={control}
          // FIXME: unformatted phone number is triggering onChange, which then considers the form dirty
          // Not currently fixing bc it's a medium lift & we may switch to a different phone number input
          render={({ field }) => (
            <Box pos="relative">
              <PhoneNumberInput value={field.value} onChange={field.onChange} disabled={branchLoading || branchError} />
              {errors.phone_number?.message && (
                <div className="absolute-error-message">{errors.phone_number.message}</div>
              )}
            </Box>
          )}
        />

        <TextInput label="Email" value={branch?.email || ''} disabled />
        <TextInput label="Metro area" value={branch?.metro_area?.name || ''} disabled />

        <Group>
          <Controller
            name="disable_notifications_outside_working_hours"
            control={control}
            render={({ field }) => (
              <Switch
                label={
                  <Group gap="5px">
                    <Text>Notifications outside working hours</Text>
                    <Tooltip label="Opt in to receive email and text notifications outside regular business hours.">
                      <ThemeIcon variant="subtle" color="gray" size="sm" mb="-3px">
                        <IconInfoCircle size={16} />
                      </ThemeIcon>
                    </Tooltip>
                  </Group>
                }
                labelPosition="left"
                py={'24px'}
                checked={!field.value}
                onChange={(event) => field.onChange(!event.currentTarget.checked)}
                disabled={branchLoading || branchError}
                styles={{
                  root: {
                    width: '100%',
                  },
                  body: {
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  },
                }}
              />
            )}
          />
        </Group>

        <Group justify="flex-end">
          <Button
            onClick={handleSubmit(onSubmit)}
            loading={updateBranchMutation.isPending}
            disabled={branchLoading || branchError || !isDirty}
          >
            Save changes
          </Button>
        </Group>
      </Stack>
    </Card>
  )
}
